"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import type { ApiProfileMin } from "@/generated/models/ApiProfileMin";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import { getSubmissionCount, getTrophyArtworkCount } from "@/helpers/artist-activity.helpers";
import { useArtistPreviewModal, type ArtistPreviewTab } from "@/hooks/useArtistPreviewModal";
import { useWaveCreatorPreviewModal } from "@/hooks/useWaveCreatorPreviewModal";
import { closeAllCustomTooltips } from "@/helpers/tooltip.helpers";
import { ArtistActivityBadge } from "./ArtistActivityBadge";
import { ArtistPreviewModal } from "./ArtistPreviewModal";
import { WaveCreatorBadge } from "./WaveCreatorBadge";
import { WaveCreatorPreviewModal } from "./WaveCreatorPreviewModal";
import type { ApiProfileClassification } from "@/generated/models/ApiProfileClassification";
import { useRouter } from "next/navigation";
interface DropAuthorBadgesProfile {
  readonly id?: string | null;
  readonly handle?: string | null;
  readonly pfp?: string | null;
  readonly banner1_color?: string | null;
  readonly banner2_color?: string | null;
  readonly cic?: number;
  readonly rep?: number;
  readonly tdh?: number;
  readonly tdh_rate?: number;
  readonly xtdh?: number;
  readonly xtdh_rate?: number;
  readonly level?: number;
  readonly primary_address?: string | null;
  readonly primary_wallet?: string | null;
  readonly archived?: boolean;
  readonly active_main_stage_submission_ids?: readonly string[] | null;
  readonly winner_main_stage_drop_ids?: readonly string[] | null;
  readonly artist_of_prevote_cards?: readonly number[] | null;
  readonly profile_wave_id?: string | null;
  readonly profile_wave_name?: string | null;
  readonly profile_wave_pfp?: string | null;
  readonly is_wave_creator?: boolean | null;
  readonly badges?: {
    readonly artist_of_main_stage_submissions?: number | null;
    readonly artist_of_memes?: number | null;
    readonly profile_wave_id?: string | null;
    readonly profile_wave_name?: string | null;
    readonly profile_wave_pfp?: string | null;
  } | null;
  readonly classification: ApiProfileClassification;
  readonly sub_classification: string | null;
}
type ApiProfileMinWithAuthorBadges = ApiProfileMin & {
  readonly badges?: DropAuthorBadgesProfile["badges"];
};
interface DropAuthorBadgesProps {
  readonly profile: DropAuthorBadgesProfile;
  readonly tooltipIdPrefix?: string | undefined;
  readonly className?: string | undefined;
  readonly size?: "default" | "compact" | undefined;
  readonly showProfileWaveDetails?: boolean | undefined;
  readonly onArtistPreviewOpen?: ((params: {
    readonly user: ApiProfileMin;
    readonly initialTab: ArtistPreviewTab;
  }) => void) | undefined;
  readonly onWaveCreatorPreviewOpen?: ((user: ApiProfileMin) => void) | undefined;
}
const DEFAULT_CONTAINER_CLASS = "tw-inline-flex tw-items-center tw-gap-x-1.5";
const getProfileWaveId = (profile: DropAuthorBadgesProfile): string | null => profile.profile_wave_id ?? profile.badges?.profile_wave_id ?? null;
const getProfileWaveName = (profile: DropAuthorBadgesProfile): string | null => profile.profile_wave_name ?? profile.badges?.profile_wave_name ?? null;
const getProfileWavePfp = (profile: DropAuthorBadgesProfile): string | null => profile.profile_wave_pfp ?? profile.badges?.profile_wave_pfp ?? null;
const toApiProfileMin = (profile: DropAuthorBadgesProfile): ApiProfileMinWithAuthorBadges => {
  const primaryAddress = profile.primary_address ?? profile.primary_wallet ?? "";
  const fallbackId = primaryAddress;
  const profileWaveId = getProfileWaveId(profile);
  return {
    id: profile.id ?? fallbackId,
    handle: profile.handle ?? null,
    pfp: profile.pfp ?? null,
    banner1_color: profile.banner1_color ?? null,
    banner2_color: profile.banner2_color ?? null,
    cic: profile.cic ?? 0,
    rep: profile.rep ?? 0,
    tdh: profile.tdh ?? 0,
    tdh_rate: profile.tdh_rate ?? 0,
    xtdh: profile.xtdh ?? 0,
    xtdh_rate: profile.xtdh_rate ?? 0,
    level: profile.level ?? 0,
    primary_address: primaryAddress,
    subscribed_actions: [],
    archived: profile.archived ?? false,
    active_main_stage_submission_ids: profile.active_main_stage_submission_ids !== null && profile.active_main_stage_submission_ids !== undefined ? [...profile.active_main_stage_submission_ids] : [],
    winner_main_stage_drop_ids: profile.winner_main_stage_drop_ids !== null && profile.winner_main_stage_drop_ids !== undefined ? [...profile.winner_main_stage_drop_ids] : [],
    artist_of_prevote_cards: profile.artist_of_prevote_cards !== null && profile.artist_of_prevote_cards !== undefined ? [...profile.artist_of_prevote_cards] : [],
    profile_wave_id: profileWaveId,
    is_wave_creator: profile.is_wave_creator === true || profileWaveId !== null,
    classification: profile.classification,
    sub_classification: profile.sub_classification,
    badges: profile.badges
  };
};
export const DropAuthorBadges: React.FC<DropAuthorBadgesProps> = t0 => {
  const $ = _c(57);
  const {
    profile,
    tooltipIdPrefix: t1,
    className: t2,
    size: t3,
    showProfileWaveDetails: t4,
    onArtistPreviewOpen,
    onWaveCreatorPreviewOpen
  } = t0;
  const tooltipIdPrefix = t1 === undefined ? "author-badges" : t1;
  const className = t2 === undefined ? DEFAULT_CONTAINER_CLASS : t2;
  const size = t3 === undefined ? "default" : t3;
  const showProfileWaveDetails = t4 === undefined ? false : t4;
  const router = useRouter();
  let t5;
  if ($[0] !== profile) {
    t5 = getProfileWaveId(profile);
    $[0] = profile;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const profileWaveId = t5;
  const submissionCount = getSubmissionCount(profile);
  const trophyCount = getTrophyArtworkCount(profile);
  const hasActivityBadge = submissionCount > 0 || trophyCount > 0;
  const isWaveCreator = profile.is_wave_creator === true || profileWaveId !== null;
  let t6;
  if ($[2] !== profile) {
    t6 = getProfileWaveName(profile);
    $[2] = profile;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const profileWaveName = t6;
  let t7;
  if ($[4] !== profile) {
    t7 = getProfileWavePfp(profile);
    $[4] = profile;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const profileWavePfp = t7;
  let t8;
  if ($[6] !== profile) {
    t8 = toApiProfileMin(profile);
    $[6] = profile;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const modalUser = t8;
  const {
    isModalOpen: isArtistPreviewOpen,
    activeTab,
    handleBadgeClick: handleArtistBadgeClick,
    handleTabChange: handleArtistTabChange,
    handleModalClose: handleArtistModalClose
  } = useArtistPreviewModal();
  const {
    isModalOpen: isWaveCreatorPreviewOpen,
    handleBadgeClick: handleWaveCreatorBadgeClick,
    handleModalClose: handleWaveCreatorModalClose
  } = useWaveCreatorPreviewModal();
  let t9;
  if ($[8] !== handleArtistBadgeClick || $[9] !== modalUser || $[10] !== onArtistPreviewOpen) {
    t9 = tab => {
      closeAllCustomTooltips();
      if (onArtistPreviewOpen) {
        onArtistPreviewOpen({
          user: modalUser,
          initialTab: tab
        });
        return;
      }
      handleArtistBadgeClick(tab);
    };
    $[8] = handleArtistBadgeClick;
    $[9] = modalUser;
    $[10] = onArtistPreviewOpen;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const onArtistBadgeClick = t9;
  let t10;
  if ($[12] !== handleWaveCreatorBadgeClick || $[13] !== modalUser || $[14] !== onWaveCreatorPreviewOpen || $[15] !== profileWaveId || $[16] !== router || $[17] !== showProfileWaveDetails) {
    t10 = () => {
      closeAllCustomTooltips();
      if (showProfileWaveDetails && profileWaveId) {
        router.push(getWaveRoute({
          waveId: profileWaveId,
          isDirectMessage: false,
          isApp: false
        }));
        return;
      }
      if (onWaveCreatorPreviewOpen) {
        onWaveCreatorPreviewOpen(modalUser);
        return;
      }
      handleWaveCreatorBadgeClick();
    };
    $[12] = handleWaveCreatorBadgeClick;
    $[13] = modalUser;
    $[14] = onWaveCreatorPreviewOpen;
    $[15] = profileWaveId;
    $[16] = router;
    $[17] = showProfileWaveDetails;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const onWaveCreatorBadgeClick = t10;
  if (!hasActivityBadge && !isWaveCreator) {
    return null;
  }
  let t11;
  if ($[19] !== hasActivityBadge || $[20] !== onArtistBadgeClick || $[21] !== size || $[22] !== submissionCount || $[23] !== tooltipIdPrefix || $[24] !== trophyCount) {
    t11 = hasActivityBadge && <ArtistActivityBadge submissionCount={submissionCount} trophyCount={trophyCount} onBadgeClick={onArtistBadgeClick} tooltipId={`${tooltipIdPrefix}-activity`} size={size} />;
    $[19] = hasActivityBadge;
    $[20] = onArtistBadgeClick;
    $[21] = size;
    $[22] = submissionCount;
    $[23] = tooltipIdPrefix;
    $[24] = trophyCount;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== isWaveCreator || $[27] !== onWaveCreatorBadgeClick || $[28] !== profileWaveId || $[29] !== profileWaveName || $[30] !== profileWavePfp || $[31] !== showProfileWaveDetails || $[32] !== size || $[33] !== tooltipIdPrefix) {
    t12 = isWaveCreator && <WaveCreatorBadge tooltipId={`${tooltipIdPrefix}-wave-creator`} onBadgeClick={onWaveCreatorBadgeClick} size={size} showWaveDetails={showProfileWaveDetails && profileWaveId !== null} waveName={profileWaveName} wavePfp={profileWavePfp} />;
    $[26] = isWaveCreator;
    $[27] = onWaveCreatorBadgeClick;
    $[28] = profileWaveId;
    $[29] = profileWaveName;
    $[30] = profileWavePfp;
    $[31] = showProfileWaveDetails;
    $[32] = size;
    $[33] = tooltipIdPrefix;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== className || $[36] !== t11 || $[37] !== t12) {
    t13 = <div className={className}>{t11}{t12}</div>;
    $[35] = className;
    $[36] = t11;
    $[37] = t12;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== activeTab || $[40] !== handleArtistModalClose || $[41] !== handleArtistTabChange || $[42] !== hasActivityBadge || $[43] !== isArtistPreviewOpen || $[44] !== modalUser || $[45] !== onArtistPreviewOpen) {
    t14 = hasActivityBadge && !onArtistPreviewOpen && <ArtistPreviewModal isOpen={isArtistPreviewOpen} onClose={handleArtistModalClose} user={modalUser} activeTab={activeTab} onTabChange={handleArtistTabChange} />;
    $[39] = activeTab;
    $[40] = handleArtistModalClose;
    $[41] = handleArtistTabChange;
    $[42] = hasActivityBadge;
    $[43] = isArtistPreviewOpen;
    $[44] = modalUser;
    $[45] = onArtistPreviewOpen;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  let t15;
  if ($[47] !== handleWaveCreatorModalClose || $[48] !== isWaveCreator || $[49] !== isWaveCreatorPreviewOpen || $[50] !== modalUser || $[51] !== onWaveCreatorPreviewOpen) {
    t15 = isWaveCreator && !onWaveCreatorPreviewOpen && <WaveCreatorPreviewModal isOpen={isWaveCreatorPreviewOpen} onClose={handleWaveCreatorModalClose} user={modalUser} />;
    $[47] = handleWaveCreatorModalClose;
    $[48] = isWaveCreator;
    $[49] = isWaveCreatorPreviewOpen;
    $[50] = modalUser;
    $[51] = onWaveCreatorPreviewOpen;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  let t16;
  if ($[53] !== t13 || $[54] !== t14 || $[55] !== t15) {
    t16 = <>{t13}{t14}{t15}</>;
    $[53] = t13;
    $[54] = t14;
    $[55] = t15;
    $[56] = t16;
  } else {
    t16 = $[56];
  }
  return t16;
};